/**
 * `<FullHistoryPanel/>` — the prominent, full-height per-task timeline (the
 * 2026-05-25 Claude-design "Variation B1": two-pane, sticky day gutter).
 *
 * Opened from the drawer's History tab ("⤢ Full view") when a user wants to
 * STUDY a task's whole history rather than glance at it. A full-screen overlay
 * over the Map (the same takeover pattern as Rewind / Snapshots); "← Back"
 * returns to the task. Left: a quiet "jump to day" gutter with counts. Right:
 * the focal-node state strip, then the day-grouped, newest-first rail at full
 * width with breathing room.
 *
 * It owns its own scope state + fetch (a self-contained surface, wired once in
 * the drawer so it works for lead and member alike). The scope toggle and the
 * subtree attribution behave exactly as in the drawer.
 *
 * Brand firewall (§A9): provenance about the work — counts are of events/days,
 * never about a person; the gutter, header, and rail never aggregate or rank
 * anyone.
 *
 * Editorial re-skin (#1043, Wave 2): theme-aware. EDITORIAL is the wireframe's
 * B3.1 state-banded panel — a serif "History" masthead with mono counts, a
 * warm-paper left rail carrying a `STATE OVER TIME` segmented bar + a duration
 * legend (both folded from the same `buildStateTimeline` model — never a new
 * projection), and the day-grouped feed on the right. LEGACY keeps the shipped
 * two-pane (jump-to-day gutter + rail), byte-identical. The structure, fetch,
 * scope behaviour, and every `data-testid` are shared.
 */

import { useMemo, useRef, useState } from "react";

import { type HistoryScope, useNodeHistory } from "../api/useNodeHistory";
import { StatusChip } from "../components/StatusChip";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import type { TaskState } from "../tree/state";
import { StateOverTimeRail } from "./StateOverTimeRail";
import { StateTimeline } from "./StateTimeline";
import { TaskTimeline } from "./TaskTimeline";
import { TimelineScopeToggle } from "./TimelineScopeToggle";
import { groupEventsByDay } from "./groupEventsByDay";

export interface FullHistoryPanelProps {
  readonly teamId: string;
  readonly nodeId: string;
  readonly nodeTitle: string;
  readonly nodeState: TaskState;
  /** Breadcrumb context: the parent's title, if any. */
  readonly parentTitle?: string | null;
  readonly members: TaskMember[];
  readonly nodeTitleById?: Map<string, string> | undefined;
  readonly teamTimezone?: string | undefined;
  /** The initial scope (mirrors the drawer's current scope). */
  readonly initialScope?: HistoryScope;
  readonly onClose: () => void;
}

export function FullHistoryPanel({
  teamId,
  nodeId,
  nodeTitle,
  nodeState,
  parentTitle = null,
  members,
  nodeTitleById,
  teamTimezone,
  initialScope = "subtree",
  onClose,
}: FullHistoryPanelProps) {
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  const [scope, setScope] = useState<HistoryScope>(initialScope);
  const { events, isLoading, isError } = useNodeHistory(teamId, nodeId, scope);
  const scrollRef = useRef<HTMLDivElement>(null);

  const focalEvents = useMemo(
    () => (scope === "subtree" ? events.filter((e) => e.node_id === nodeId) : events),
    [events, scope, nodeId],
  );
  const groups = useMemo(
    () => groupEventsByDay(events, { tz: teamTimezone }),
    [events, teamTimezone],
  );

  const jumpToDay = (key: string) => {
    scrollRef.current
      ?.querySelector(`[data-day-key="${CSS.escape(key)}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid="full-history-panel"
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ backgroundColor: editorial ? c.canvas : PALETTE.canvas }}
      aria-label={`${nodeTitle} — full history`}
    >
      {/* Header */}
      <header
        className="flex items-center gap-4 border-b px-6 py-4"
        style={{
          borderColor: editorial ? c.rule : PALETTE.cardBorder,
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
        }}
      >
        <button
          type="button"
          data-testid="full-history-back"
          onClick={onClose}
          className="rounded-md px-2 py-1 text-sm font-semibold transition-opacity hover:opacity-80"
          style={{ color: editorial ? c.teal : PALETTE.teal }}
        >
          ← Back
        </button>
        <div className="min-w-0 flex-1">
          <div
            className={editorial ? "truncate text-[11px]" : "truncate font-mono text-[11px]"}
            style={
              editorial
                ? { color: c.faint, fontFamily: theme.fonts.mono, letterSpacing: "0.02em" }
                : { color: PALETTE.faint }
            }
          >
            {parentTitle ? `${parentTitle}  ›  ` : ""}
            {nodeTitle}
          </div>
          <div className="flex items-center gap-2.5">
            <span
              className={editorial ? "text-[26px]" : "text-lg font-bold"}
              style={
                editorial
                  ? {
                      color: c.ink,
                      fontFamily: theme.fonts.serif,
                      fontStyle: "italic",
                      lineHeight: 1.1,
                    }
                  : { color: PALETTE.ink }
              }
            >
              History
            </span>
            <StatusChip state={nodeState} />
          </div>
        </div>
        <TimelineScopeToggle scope={scope} onChange={setScope} />
        <div
          className={
            editorial
              ? "hidden text-right text-[11px] leading-relaxed sm:block"
              : "hidden text-right font-mono text-[11px] leading-relaxed sm:block"
          }
          style={
            editorial
              ? { color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.02em" }
              : { color: PALETTE.muted }
          }
          data-testid="full-history-counts"
        >
          {events.length} event{events.length === 1 ? "" : "s"}
          <br />
          {groups.length} day{groups.length === 1 ? "" : "s"}
        </div>
      </header>

      {/* Body */}
      {isLoading ? (
        <PanelMessage testId="full-history-loading" title="Loading activity…">
          A calm record of what changed and when.
        </PanelMessage>
      ) : isError ? (
        <PanelMessage testId="full-history-error" title="History isn’t available here yet.">
          The story of this task will appear here once it’s switched on.
        </PanelMessage>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left rail. EDITORIAL: the wireframe's `STATE OVER TIME` segmented
              bar + duration legend (folded from the same timeline model), then
              the jump-to-day list. LEGACY: the shipped jump-to-day gutter,
              byte-identical. Both keep the `full-history-gutter*` testids. */}
          {editorial ? (
            <nav
              data-testid="full-history-gutter"
              className="hidden w-[240px] shrink-0 overflow-auto border-r px-5 py-5 md:block"
              style={{ borderColor: c.rule, backgroundColor: c.canvas }}
              aria-label="State over time and jump to day"
            >
              <StateOverTimeRail events={focalEvents} theme={theme} />
              <div
                className="mt-5 mb-2 text-[10px] uppercase"
                style={{ color: c.faint, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }}
              >
                Jump to day
              </div>
              {groups.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  data-testid="full-history-gutter-day"
                  onClick={() => jumpToDay(g.key)}
                  className="mb-0.5 block w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white"
                >
                  <div
                    className="text-[11px] font-semibold uppercase"
                    style={{ color: c.ink, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }}
                  >
                    {g.label}
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: c.faint, fontFamily: theme.fonts.mono }}
                  >
                    {g.dateLabel} · {g.events.length}
                  </div>
                </button>
              ))}
            </nav>
          ) : (
            <nav
              data-testid="full-history-gutter"
              className="hidden w-[200px] shrink-0 overflow-auto border-r px-4 py-5 md:block"
              style={{ borderColor: PALETTE.cardBorder }}
              aria-label="Jump to day"
            >
              <div
                className="mb-3 font-mono text-[10px] uppercase tracking-wide"
                style={{ color: PALETTE.faint }}
              >
                Jump to day
              </div>
              {groups.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  data-testid="full-history-gutter-day"
                  onClick={() => jumpToDay(g.key)}
                  className="mb-0.5 block w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white"
                >
                  <div className="text-[13px] font-semibold" style={{ color: PALETTE.slate }}>
                    {g.label}
                  </div>
                  <div className="font-mono text-[11px]" style={{ color: PALETTE.faint }}>
                    {g.dateLabel} · {g.events.length}
                  </div>
                </button>
              ))}
            </nav>
          )}

          {/* The rich event stream. */}
          <div
            ref={scrollRef}
            className="min-w-0 flex-1 overflow-auto px-6 py-5 md:px-10"
            style={editorial ? { backgroundColor: c.cardBg } : undefined}
          >
            <div className="mx-auto max-w-[680px]">
              <StateTimeline nodeTitle={nodeTitle} events={focalEvents} />
              <div className="mt-5">
                {events.length === 0 ? (
                  <PanelMessage testId="full-history-empty" title="No activity yet." inline>
                    {scope === "subtree"
                      ? "As this task and its sub-tasks change, their story shows up here."
                      : "As the team updates this task, its story shows up here."}
                  </PanelMessage>
                ) : (
                  <TaskTimeline
                    events={events}
                    members={members}
                    nodeTitleById={nodeTitleById}
                    focalNodeId={nodeId}
                    showNodeAttribution={scope === "subtree"}
                    tz={teamTimezone}
                  />
                )}
              </div>
              {/* The wireframe B3.1 footer framing (editorial only). */}
              {editorial && (
                <div
                  data-testid="full-history-footer"
                  className="mt-6 border-t pt-4 text-[11px] italic"
                  style={{
                    borderColor: c.rule,
                    color: c.faint,
                    fontFamily: theme.fonts.mono,
                    letterSpacing: "0.02em",
                  }}
                >
                  Reads as a story of the work — never a tally of a person.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calm message state ─────────────────────────────────────────────────────────

function PanelMessage({
  testId,
  title,
  children,
  inline = false,
}: {
  testId: string;
  title: string;
  children: React.ReactNode;
  inline?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-2 text-center ${
        inline ? "py-12" : "flex-1 py-20"
      }`}
    >
      <span className="text-3xl" aria-hidden>
        🕑
      </span>
      <div className="text-base font-semibold" style={{ color: PALETTE.slate }}>
        {title}
      </div>
      <div className="max-w-[22rem] text-sm" style={{ color: PALETTE.faint }}>
        {children}
      </div>
    </div>
  );
}
